"use client";

import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

// Background image configuration
interface BackgroundImage {
  src: string;
  alt: string;
  brightness?: number;
  scale?: number; // Custom zoom level (default 1.1)
  objectPosition?: string; // CSS object-position (default "center center")
  positionX?: string; // Horizontal position: "left", "center", "right", or percentage (e.g. "30%")
  positionY?: string; // Vertical position: "top", "center", "bottom", or percentage (e.g. "60%")
}

interface MaskEffectProps {
  normalText: string[];
  shadowText: string[];
  greyIndices?: number[];
  bitcoinIndices?: number[]; // Für Bitcoin-Orange Färbung
  pressedInIndices?: number[]; // Für hineingedrückten Text-Effekt mit Neon-Flicker
  normalGradient?: string; // CSS gradient oder Farbe (z.B. "#0d0d0d" oder "linear-gradient(...)")
  shadowGradient?: string; // CSS gradient oder Farbe (z.B. "#F7931A" oder "linear-gradient(...)")
  textColor?: string; // Textfarbe für normalText (default: #b7ab98)
  className?: string;
  backgroundImages?: BackgroundImage[];
  imageRotateInterval?: number; // in milliseconds
  backgroundBrightness?: number; // 0-1, global brightness for all background images (default 0.5)
  // flatMode: deaktiviert den eigenen Cursor-Mask-Effekt (kein shadow-Layer, kein cursor-none).
  // Wird auf der Landingpage genutzt, wenn der globale LandingMaskOverlay den Reveal übernimmt.
  flatMode?: boolean;
}

// Komponente für den flackernden Neon-Text (SVG-basiert für echten Inset-Effekt)
interface FlickerTextProps {
  word: string;
  hasBitcoinGlow: boolean;
}

function FlickerText({ word, hasBitcoinGlow }: FlickerTextProps) {
  const letterAnimations: Record<string, { dur: string; keyTimes: string }> = {
    B: { dur: "18s", keyTimes: "0;0.42;0.4208;0.4291;1" },
    i: { dur: "12s", keyTimes: "0;0.25;0.2512;0.2637;1" },
    t: { dur: "15s", keyTimes: "0;0.55;0.551;0.561;1" },
    c: { dur: "10s", keyTimes: "0;0.70;0.7015;0.7165;1" },
    o: { dur: "14s", keyTimes: "0;0.35;0.3511;0.3618;1" },
    n: { dur: "11s", keyTimes: "0;0.48;0.4814;0.495;1" },
  };

  const getAnimation = (char: string, index: number) => {
    if (letterAnimations[char]) return letterAnimations[char];
    const durations = ["10s", "11s", "12s", "13s", "14s", "15s", "16s", "17s", "18s"];
    const dur = durations[index % durations.length];
    const startPoint = (0.2 + ((index * 0.15) % 0.6)).toFixed(2);
    const midPoint = (parseFloat(startPoint) + 0.01).toFixed(4);
    const endPoint = (parseFloat(startPoint) + 0.02).toFixed(4);
    return { dur, keyTimes: `0;${startPoint};${midPoint};${endPoint};1` };
  };

  const filterId = `inset-text-${word}`;
  const glowFilterId = `neon-glow-${word}`;

  // CSS-basierter Pressed-In-Effekt (ohne SVG für korrekte Größe)
  // Der Glow wird mit einem separaten SVG-Overlay realisiert
  const pressedInStyle: React.CSSProperties = {
    background: "linear-gradient(145deg, #0a0a0a 0%, #151515 40%, #252525 100%)",
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
    textShadow: "3px 3px 6px rgba(0,0,0,0.9), -1px -1px 2px rgba(255,255,255,0.03)",
  };

  if (!hasBitcoinGlow) {
    return <span style={pressedInStyle}>{word}</span>;
  }

  // Mit Glow: CSS-Text + SVG-Overlay nur für den Flicker-Effekt
  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      {/* Basis-Text mit CSS Pressed-In Effekt (korrekte Größe) */}
      <span style={pressedInStyle}>{word}</span>

      {/* SVG-Overlay NUR für den Flicker-Glow (positioniert sich über dem Text) */}
      <svg
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: "100%",
          height: "100%",
          overflow: "visible",
          pointerEvents: "none",
        }}
      >
        <defs>
          <clipPath id={glowFilterId}>
            <text
              x="50%"
              y="75%"
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="1em"
              fontWeight={700}
              fontFamily="'Avant Garde', sans-serif"
              letterSpacing="-0.05em"
            >
              {word}
            </text>
          </clipPath>
        </defs>

        {/* Flackernder Neon-Glow */}
        <text
          x="50%"
          y="75%"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="1em"
          fontWeight={700}
          fontFamily="'Avant Garde', sans-serif"
          letterSpacing="-0.05em"
          fill="none"
          stroke="#F7931A"
          strokeWidth="0.02em"
          clipPath={`url(#${glowFilterId})`}
        >
          {word.split("").map((char, i) => {
            const anim = getAnimation(char, i);
            return (
              <tspan key={i} strokeOpacity="0">
                {char}
                <animate
                  attributeName="stroke-opacity"
                  values="0;0;1;0;0"
                  keyTimes={anim.keyTimes}
                  dur={anim.dur}
                  repeatCount="indefinite"
                />
              </tspan>
            );
          })}
        </text>
      </svg>
    </span>
  );
}

// Helper: Konvertiert einfache Farben oder Gradients zu CSS background
function parseGradient(value: string): string {
  // Wenn es bereits ein Gradient ist, direkt verwenden
  if (value.includes("gradient")) {
    return value;
  }
  // Wenn es eine einzelne Farbe ist (hex, rgb, etc.), als Solid-Background verwenden
  if (
    value.startsWith("#") ||
    value.startsWith("rgb") ||
    value.startsWith("hsl")
  ) {
    return value;
  }
  // Fallback: Als Farbe behandeln
  return value;
}

export const MaskEffect = forwardRef<HTMLElement, MaskEffectProps>(function MaskEffect({
  normalText,
  shadowText,
  greyIndices = [],
  bitcoinIndices = [],
  pressedInIndices = [],
  normalGradient = "#1f2937", // Default: dark gray
  shadowGradient = "#3b82f6", // Default: blue
  textColor = "#b7ab98", // Default: beige (dark mode)
  className = "",
  backgroundImages = [],
  imageRotateInterval = 8000,
  backgroundBrightness = 0.5,
  flatMode = false,
}: MaskEffectProps, ref) {
  const navigate = useNavigate();
  const [isInitialized, setIsInitialized] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const shadowRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLElement>(null);
  // Externer ref (forwardRef) auf das section-Element exposen
  useImperativeHandle(ref, () => containerRef.current!, []);
  const animationRef = useRef<number | undefined>(undefined);
  const mousePositionRef = useRef({ x: 50, y: 50 });
  const maskPositionRef = useRef({ x: 50, y: 50 });
  const currentSizeRef = useRef(0);
  const targetSizeRef = useRef(0);
  const globalMousePosition = useRef({ x: 0, y: 0 });
  const isAnimatingRef = useRef(false);
  const containerRectRef = useRef<DOMRect | null>(null);
  const isMouseInContainerRef = useRef(false);

  // Magnetic-Snap: hält das aktuell gesnappte Target-Element (für Click-Navigation).
  // Wenn gesetzt, ist die Maske auf dieses Element gezogen und ein Klick soll dorthin navigieren.
  const snappedTargetRef = useRef<HTMLElement | null>(null);

  // Speed-Boost beim Snap-Einrasten: zählt die Frames runter in denen der Lerp
  // doppelt so schnell läuft, damit die Maske sichtbar "reinspringt".
  const snapBoostRef = useRef(0);

  // Setzt das gesnappte Target. Das body[data-mask-snapped]-Attribut wird NICHT hier,
  // sondern in der Animation-Loop gesetzt - erst wenn die Maskenposition tatsaechlich
  // das Icon erreicht hat. So "ueberlagert" der Farbwechsel den Kreis genau im Moment
  // des Ankommens (nicht schon beim Eintritt in den Snap-Radius).
  const setSnappedTarget = useCallback((el: HTMLElement | null) => {
    if (snappedTargetRef.current === el) return;
    snappedTargetRef.current = el;
    if (el) {
      // Snap-In: Boost fuer ~10 Frames (~160ms), damit Maske sichtbar reinspringt
      snapBoostRef.current = 10;
    } else {
      // De-Snap: sofort Attribut entfernen, damit Kreis bei Weggang zurueck wird
      document.body.removeAttribute("data-mask-snapped");
    }
  }, []);

  // Findet das nächste [data-mask-snap-target] im Snap-Radius. Abstand wird vom Target-RAND
  // gemessen (nicht Zentrum), damit große/kleine Targets fair snappen.
  // Wird von Maus- UND Touch-Handlern genutzt, daher auf Component-Ebene via useCallback.
  const SNAP_RADIUS = 80; // px - Schnapp-Distanz (hier springt die Maske ins Target)
  const PULL_RADIUS = 150; // px - Magnetic-Pull-Distanz (hier beginnt das Icon sich zu bewegen)
  // Maske zieht das Icon spuerbar Richtung Cursor.
  const PULL_MAX = 100; // px - maximale Icon-Translation Richtung Cursor
  const findSnapTarget = useCallback(
    (
      clientX: number,
      clientY: number
    ): {
      element: HTMLElement;
      centerX: number;
      centerY: number;
      size: number;
    } | null => {
      const targets = document.querySelectorAll<HTMLElement>(
        "[data-mask-snap-target]"
      );
      if (targets.length === 0) return null;

      let bestEl: HTMLElement | null = null;
      let bestCenterX = 0;
      let bestCenterY = 0;
      let bestSize = 0;
      let bestDist = Infinity;

      for (const el of Array.from(targets)) {
        const rect = el.getBoundingClientRect();
        const dx = Math.max(rect.left - clientX, 0, clientX - rect.right);
        const dy = Math.max(rect.top - clientY, 0, clientY - rect.bottom);
        const edgeDist = Math.sqrt(dx * dx + dy * dy);
        if (edgeDist > SNAP_RADIUS) continue;
        if (edgeDist < bestDist) {
          bestEl = el;
          bestCenterX = rect.left + rect.width / 2;
          bestCenterY = rect.top + rect.height / 2;
          // Target-Durchmesser = groesserer der beiden Dimensionen (Kreise sind quadratisch).
          bestSize = Math.max(rect.width, rect.height);
          bestDist = edgeDist;
        }
      }

      if (!bestEl) return null;
      return {
        element: bestEl,
        centerX: bestCenterX,
        centerY: bestCenterY,
        size: bestSize,
      };
    },
    []
  );

  // Magnetic Pull: Mask-Cursor zieht das Icon Richtung Cursor (max. PULL_MAX px).
  // Beim Snap-Einrasten wird die Auslenkung im Moment des Einschlags als Snapshot
  // eingefroren (--pull-snapshot-x/y). Die CSS-Keyframe-Animation startet dann von
  // diesem Snapshot-Vektor und pendelt mit mehreren abgedaempften Schwingungen
  // zurueck in die Ruhelage - funktioniert aus jeder Richtung, weil der Snapshot
  // den Vektor speichert.
  const applyMagneticPull = useCallback(
    (clientX: number, clientY: number, snappedEl: HTMLElement | null) => {
      const targets = document.querySelectorAll<HTMLElement>(
        "[data-mask-snap-target]"
      );
      targets.forEach((el) => {
        if (el === snappedEl) {
          // Snap-Einrasten: Auslenkung nur EINMAL einfrieren (beim Zustandswechsel),
          // dann Spring-Animation ueber Klassenwechsel triggern. Laufende Animations
          // werden per reflow neu gestartet (siehe CSS: animation auf .is-snap-locked).
          if (!el.classList.contains("is-snap-locked")) {
            // Aktuelle Pull-Werte aus den CSS-Vars lesen (als Snapshot sichern)
            const cs = getComputedStyle(el);
            const snapX = cs.getPropertyValue("--pull-x").trim() || "0px";
            const snapY = cs.getPropertyValue("--pull-y").trim() || "0px";
            el.style.setProperty("--pull-snapshot-x", snapX);
            el.style.setProperty("--pull-snapshot-y", snapY);
            // Pull auf 0 zuruecksetzen - ab jetzt uebernimmt die Spring-Keyframe
            el.style.setProperty("--pull-x", "0px");
            el.style.setProperty("--pull-y", "0px");
            el.classList.add("is-snap-locked");
          }
          return;
        }
        // Nicht (mehr) gesnappt: Spring-Klasse entfernen fuer sanftes Annaehern.
        el.classList.remove("is-snap-locked");
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = clientX - cx;
        const dy = clientY - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > PULL_RADIUS) {
          el.style.setProperty("--pull-x", "0px");
          el.style.setProperty("--pull-y", "0px");
          return;
        }
        // Smoothstep-Falloff: linear am Rand, stärker in der Mitte
        const t = 1 - dist / PULL_RADIUS; // 0..1
        const strength = t * t * (3 - 2 * t); // smoothstep
        const unit = dist > 0 ? 1 / dist : 0;
        const pullX = dx * unit * strength * PULL_MAX;
        const pullY = dy * unit * strength * PULL_MAX;
        el.style.setProperty("--pull-x", `${pullX.toFixed(1)}px`);
        el.style.setProperty("--pull-y", `${pullY.toFixed(1)}px`);
      });
    },
    []
  );

  // Parallax: Refs statt State um Re-Renders zu vermeiden
  const parallaxTargetRef = useRef({ x: 0, y: 0 });
  const parallaxCurrentRef = useRef({ x: 0, y: 0 });
  const parallaxAnimationRef = useRef<number | undefined>(undefined);
  const parallaxContainerRef = useRef<HTMLDivElement>(null);

  // Gecachte Collision-Rects (werden nur bei Resize aktualisiert)
  const collisionRectsRef = useRef<DOMRect[]>([]);

  // Touch-Device Detection und Steuerung (nur Tap-and-Hold)
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [touchControlActive, setTouchControlActive] = useState(false);
  const touchHoldTimerRef = useRef<number | undefined>(undefined);
  const touchStartPosRef = useRef({ x: 0, y: 0 });

  // Viewport-Höhe messen (zuverlässiger als CSS vh/svh auf Tablets/Mobile)
  // WICHTIG: Nur bei echtem Resize (Breitenänderung) aktualisieren, nicht beim Scrollen
  // (Browser-UI ein-/ausblenden ändert nur die Höhe, nicht die Breite)
  useEffect(() => {
    let lastWidth = window.innerWidth;

    const updateViewportHeight = () => {
      setViewportHeight(window.innerHeight);
      lastWidth = window.innerWidth;
    };

    const handleResize = () => {
      // Nur aktualisieren wenn sich die Breite geändert hat (echter Resize)
      // Ignorieren wenn nur die Höhe sich ändert (Browser-UI beim Scrollen)
      if (window.innerWidth !== lastWidth) {
        updateViewportHeight();
      }
    };

    const handleOrientationChange = () => {
      // Bei Orientierungswechsel immer aktualisieren
      // Kurze Verzögerung damit der Browser die neue Größe berechnen kann
      setTimeout(updateViewportHeight, 100);
    };

    // Initial messen
    updateViewportHeight();

    // Bei echtem Resize und Orientierungswechsel aktualisieren
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, []);

  // Touch-Device Detection (einfach - nur für Tap-and-Hold)
  useEffect(() => {
    const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    setIsTouchDevice(hasTouch);
  }, []);

  // Touch-Handler - Tap-and-Hold für manuelle Steuerung
  useEffect(() => {
    if (!isTouchDevice) return;

    const container = containerRef.current;
    const shadow = shadowRef.current;
    if (!container || !shadow) return;

    const HOLD_DURATION = 400; // ms bis Touch-Control aktiviert wird
    const MOVE_THRESHOLD = 10; // px Bewegung die als Scroll gilt

    // Kontextmenü verhindern (langes Drücken)
    const handleContextMenu = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    // Native Touch-Callout verhindern
    container.addEventListener("contextmenu", handleContextMenu);

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;

      // NICHT preventDefault hier - das würde Scrollen verhindern
      // Kontextmenu wird durch CSS (WebkitTouchCallout) und contextmenu-Event verhindert

      touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };

      // Position für später speichern
      const startX = touch.clientX;
      const startY = touch.clientY;

      // Timer starten für Tap-and-Hold
      touchHoldTimerRef.current = window.setTimeout(() => {
        // Vibration-Feedback (wenn verfügbar)
        if ("vibrate" in navigator) {
          navigator.vibrate(50);
        }

        setTouchControlActive(true);

        // Collision-Rects aktualisieren für Touch
        const changeTexts = container.querySelectorAll(".change-text");
        collisionRectsRef.current = Array.from(changeTexts).map((el) =>
          el.getBoundingClientRect()
        );

        // Collision-Detection für initiale Größe
        // Tablet-Bereich erweitert: iPad Pro Querformat ist 1366px
        const isTablet = window.innerWidth >= 768 && window.innerWidth <= 1400;
        const isSmartphone = window.innerWidth < 768;
        const padding = isTablet ? -40 : 0;
        let isOverText = false;

        for (const textRect of collisionRectsRef.current) {
          if (
            startX >= textRect.left - padding &&
            startX <= textRect.right + padding &&
            startY >= textRect.top - padding &&
            startY <= textRect.bottom + padding
          ) {
            isOverText = true;
            break;
          }
        }

        // Magnetic Snap auch auf Touch: Finger nah am Icon -> Maske zieht sich ans Icon.
        const snap = findSnapTarget(startX, startY);
        let effectiveX = startX;
        let effectiveY = startY;
        if (snap) {
          effectiveX = snap.centerX;
          effectiveY = snap.centerY;
          setSnappedTarget(snap.element);
        } else {
          setSnappedTarget(null);
        }

        // Größe basierend auf Position
        if (snap) {
          // Maske schrumpft EXAKT auf Target-Groesse -> sieht aus als waere sie im Kreis verschwunden
          targetSizeRef.current = snap.size;
        } else if (isOverText) {
          targetSizeRef.current = isSmartphone ? 200 : isTablet ? 320 : 450;
        } else {
          targetSizeRef.current = isSmartphone ? 50 : isTablet ? 60 : 100;
        }

        // Position auf Touch setzen (ggf. auf Snap-Zentrum verschoben)
        const rect =
          containerRectRef.current || container.getBoundingClientRect();
        const relativeX = ((effectiveX - rect.left) / rect.width) * 100;
        const relativeY = ((effectiveY - rect.top) / rect.height) * 100;
        mousePositionRef.current = { x: relativeX, y: relativeY };

        // Animation starten
        if (!isAnimatingRef.current) {
          isAnimatingRef.current = true;
          animationRef.current = requestAnimationFrame(function animate() {
            const deltaX =
              mousePositionRef.current.x - maskPositionRef.current.x;
            const deltaY =
              mousePositionRef.current.y - maskPositionRef.current.y;

            maskPositionRef.current.x += deltaX * 0.15;
            maskPositionRef.current.y += deltaY * 0.15;

            const sizeDelta = targetSizeRef.current - currentSizeRef.current;
            currentSizeRef.current += sizeDelta * 0.12;

            shadow.style.setProperty("--posX", `${maskPositionRef.current.x}%`);
            shadow.style.setProperty("--posY", `${maskPositionRef.current.y}%`);
            shadow.style.setProperty("--size", `${currentSizeRef.current}px`);

            if (
              Math.abs(deltaX) > 0.1 ||
              Math.abs(deltaY) > 0.1 ||
              Math.abs(sizeDelta) > 0.5
            ) {
              animationRef.current = requestAnimationFrame(animate);
            } else {
              isAnimatingRef.current = false;
            }
          });
        }
      }, HOLD_DURATION);
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;

      const moveX = Math.abs(touch.clientX - touchStartPosRef.current.x);
      const moveY = Math.abs(touch.clientY - touchStartPosRef.current.y);

      // Wenn zu viel Bewegung vor Hold-Aktivierung -> Scroll, Timer abbrechen
      if (
        !touchControlActive &&
        (moveX > MOVE_THRESHOLD || moveY > MOVE_THRESHOLD)
      ) {
        if (touchHoldTimerRef.current) {
          clearTimeout(touchHoldTimerRef.current);
          touchHoldTimerRef.current = undefined;
        }
        return;
      }

      // Wenn Touch-Control aktiv -> Maske folgt dem Finger
      if (touchControlActive) {
        e.preventDefault(); // Scroll verhindern im Control-Modus

        const rect =
          containerRectRef.current || container.getBoundingClientRect();

        // Magnetic Snap: bewegter Finger nah am Icon -> Maske zieht sich ans Icon.
        const snap = findSnapTarget(touch.clientX, touch.clientY);
        let effectiveX = touch.clientX;
        let effectiveY = touch.clientY;
        if (snap) {
          effectiveX = snap.centerX;
          effectiveY = snap.centerY;
          setSnappedTarget(snap.element);
        } else {
          setSnappedTarget(null);
        }

        const relativeX = ((effectiveX - rect.left) / rect.width) * 100;
        const relativeY = ((effectiveY - rect.top) / rect.height) * 100;

        // Grenzen einhalten
        const clampedX = Math.max(0, Math.min(100, relativeX));
        const clampedY = Math.max(0, Math.min(100, relativeY));

        mousePositionRef.current = { x: clampedX, y: clampedY };

        // Collision-Detection für dynamische Größe
        // Tablet-Bereich erweitert: iPad Pro Querformat ist 1366px
        const isTablet = window.innerWidth >= 768 && window.innerWidth <= 1400;
        const isSmartphone = window.innerWidth < 768;
        const padding = isTablet ? -40 : 0;
        let isOverText = false;

        for (const textRect of collisionRectsRef.current) {
          if (
            touch.clientX >= textRect.left - padding &&
            touch.clientX <= textRect.right + padding &&
            touch.clientY >= textRect.top - padding &&
            touch.clientY <= textRect.bottom + padding
          ) {
            isOverText = true;
            break;
          }
        }

        // Größe anpassen: Snap > Text > Kleine Maske
        if (snap) {
          // Maske schrumpft EXAKT auf Target-Groesse
          targetSizeRef.current = snap.size;
        } else if (isOverText) {
          targetSizeRef.current = isSmartphone ? 200 : isTablet ? 320 : 450;
        } else {
          targetSizeRef.current = isSmartphone ? 50 : isTablet ? 60 : 100; // Kleinere Maske wenn nicht über Text
        }

        // Animation neu starten falls gestoppt (wichtig wenn Finger pausiert und dann weiterbewegt)
        if (!isAnimatingRef.current) {
          isAnimatingRef.current = true;
          animationRef.current = requestAnimationFrame(function animate() {
            const deltaX =
              mousePositionRef.current.x - maskPositionRef.current.x;
            const deltaY =
              mousePositionRef.current.y - maskPositionRef.current.y;

            maskPositionRef.current.x += deltaX * 0.15;
            maskPositionRef.current.y += deltaY * 0.15;

            const sizeDelta = targetSizeRef.current - currentSizeRef.current;
            currentSizeRef.current += sizeDelta * 0.12;

            shadow.style.setProperty("--posX", `${maskPositionRef.current.x}%`);
            shadow.style.setProperty("--posY", `${maskPositionRef.current.y}%`);
            shadow.style.setProperty("--size", `${currentSizeRef.current}px`);

            if (
              Math.abs(deltaX) > 0.1 ||
              Math.abs(deltaY) > 0.1 ||
              Math.abs(sizeDelta) > 0.5
            ) {
              animationRef.current = requestAnimationFrame(animate);
            } else {
              isAnimatingRef.current = false;
            }
          });
        }
      }
    };

    const handleTouchEnd = () => {
      // Timer abbrechen
      if (touchHoldTimerRef.current) {
        clearTimeout(touchHoldTimerRef.current);
        touchHoldTimerRef.current = undefined;
      }

      // Snap-Navigation bei Touch-Release: wenn der Finger beim Loslassen
      // auf einem gesnappten Target war, navigiere dorthin (der native Click
      // kommt nicht durch, weil preventDefault im Control-Modus aktiv ist).
      const snappedEl = snappedTargetRef.current;
      if (snappedEl) {
        const href = snappedEl.getAttribute("href");
        if (href) navigate(href);
        setSnappedTarget(null);
      }

      // Touch-Control deaktivieren - Maske schrumpft auf 0
      if (touchControlActive) {
        setTouchControlActive(false);
        targetSizeRef.current = 0;
      }
    };

    // passive: false für touchstart damit preventDefault funktioniert
    container.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("touchend", handleTouchEnd, { passive: true });
    container.addEventListener("touchcancel", handleTouchEnd, {
      passive: true,
    });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
      container.removeEventListener("touchcancel", handleTouchEnd);
      container.removeEventListener("contextmenu", handleContextMenu);
      if (touchHoldTimerRef.current) {
        clearTimeout(touchHoldTimerRef.current);
      }
    };
  }, [isTouchDevice, touchControlActive, navigate, findSnapTarget, setSnappedTarget]);

  // Auto-rotate background images
  useEffect(() => {
    if (backgroundImages.length <= 1) return;

    const interval = setInterval(() => {
      setActiveImageIndex((prev) => (prev + 1) % backgroundImages.length);
    }, imageRotateInterval);

    return () => clearInterval(interval);
  }, [backgroundImages.length, imageRotateInterval]);

  // Parallax animation loop - direkte DOM-Manipulation statt React State
  const updateParallax = useCallback(() => {
    const deltaX = parallaxTargetRef.current.x - parallaxCurrentRef.current.x;
    const deltaY = parallaxTargetRef.current.y - parallaxCurrentRef.current.y;

    // Smooth easing
    const easing = 0.08;
    parallaxCurrentRef.current.x += deltaX * easing;
    parallaxCurrentRef.current.y += deltaY * easing;

    // Direkte DOM-Aktualisierung statt setState (verhindert Re-Renders)
    const container = parallaxContainerRef.current;
    if (container) {
      const imgs = container.querySelectorAll<HTMLImageElement>("[data-parallax-img]");
      imgs.forEach((img) => {
        const baseTransform = img.dataset.baseTransform || "";
        img.style.transform = `translate(${parallaxCurrentRef.current.x}px, ${parallaxCurrentRef.current.y}px) ${baseTransform}`;
      });
    }

    // Continue animation if still moving
    if (Math.abs(deltaX) > 0.01 || Math.abs(deltaY) > 0.01) {
      parallaxAnimationRef.current = requestAnimationFrame(updateParallax);
    } else {
      parallaxAnimationRef.current = undefined;
    }
  }, []);

  // Handle mouse move for parallax
  useEffect(() => {
    if (backgroundImages.length === 0) return;

    const handleParallaxMouseMove = (event: MouseEvent) => {
      const { clientX, clientY } = event;
      const rect = containerRectRef.current;

      if (!rect) return;

      // Calculate position relative to center (-1 to 1)
      const relativeX = ((clientX - rect.left) / rect.width - 0.5) * 2;
      const relativeY = ((clientY - rect.top) / rect.height - 0.5) * 2;

      // Invert for natural parallax feel (move opposite to mouse)
      // Scale: max 15px movement
      const maxOffset = 15;
      parallaxTargetRef.current = {
        x: -relativeX * maxOffset,
        y: -relativeY * maxOffset,
      };

      // Start animation if not running
      if (!parallaxAnimationRef.current) {
        parallaxAnimationRef.current = requestAnimationFrame(updateParallax);
      }
    };

    document.addEventListener("mousemove", handleParallaxMouseMove);

    return () => {
      document.removeEventListener("mousemove", handleParallaxMouseMove);
      if (parallaxAnimationRef.current) {
        cancelAnimationFrame(parallaxAnimationRef.current);
      }
    };
  }, [backgroundImages.length, updateParallax]);

  // Direkt initialisieren - opacity geht sofort auf 1, unabhängig vom Mask-Setup
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    const shadow = shadowRef.current;
    const container = containerRef.current;

    // flatMode: Hero hat keinen eigenen Mask-Effekt - der globale LandingMaskOverlay
    // übernimmt das. useEffect komplett überspringen.
    if (flatMode) return;

    if (!shadow || !container) return;

    // SOFORTIGE Initialisierung für Maske - KEIN Loading-State!
    shadow.style.setProperty("--posX", "50%");
    shadow.style.setProperty("--posY", "50%");
    shadow.style.setProperty("--size", "0px"); // Startet mit 0, wächst bei Maus-Hover
    shadow.style.opacity = "1"; // Shadow-Layer immer aktiv für Maske

    // Responsive Größenberechnung (wie CSS clamp)
    const getResponsiveSize = (
      min: number,
      vwPercent: number,
      max: number
    ): number => {
      const vwSize = window.innerWidth * (vwPercent / 100);
      return Math.min(Math.max(min, vwSize), max);
    };

    // Tablet-Detection (768px - 1400px für alle Tablets inkl. iPad Pro Querformat)
    const isTabletSize = (): boolean => {
      return window.innerWidth >= 768 && window.innerWidth <= 1400;
    };

    // Collision-Rects cachen (verhindert Layout-Thrashing)
    const updateCollisionRects = () => {
      const changeTexts = container.querySelectorAll(".change-text");
      collisionRectsRef.current = Array.from(changeTexts).map((el) =>
        el.getBoundingClientRect()
      );
    };

    // Kollisionserkennung mit gecachten Rects
    // Auf Tablets: negatives Padding damit Maske später (näher am Text) groß wird
    const detectCollision = (clientX: number, clientY: number): boolean => {
      const padding = isTabletSize() ? -40 : 0; // -40px auf Tablets = muss näher ran

      for (const rect of collisionRectsRef.current) {
        if (
          clientX >= rect.left - padding &&
          clientX <= rect.right + padding &&
          clientY >= rect.top - padding &&
          clientY <= rect.bottom + padding
        ) {
          return true;
        }
      }
      return false;
    };

    // Optimierter Container Rect Cache
    const updateContainerRect = () => {
      containerRectRef.current = container.getBoundingClientRect();
      // Auch Collision-Rects aktualisieren
      updateCollisionRects();
    };

    // Initiales Rect cachen
    updateContainerRect();

    // Frame-rate-unabhängige Animation mit zeitbasiertem Lerp
    let lastFrameTime = 0;
    const LERP_SPEED = 6; // Höher = schneller (Position)
    const SIZE_LERP_SPEED = 8; // Höher = schneller (Größe)

    const updateMaskPosition = (timestamp: number) => {
      if (!lastFrameTime) lastFrameTime = timestamp;
      const dt = Math.min((timestamp - lastFrameTime) / 1000, 0.05); // Delta in Sekunden, max 50ms
      lastFrameTime = timestamp;

      const deltaX = mousePositionRef.current.x - maskPositionRef.current.x;
      const deltaY = mousePositionRef.current.y - maskPositionRef.current.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // Zeitbasierter Lerp-Faktor: 1 - e^(-speed * dt).
      // Boost beim Snap-Einrasten: 3-fache Geschwindigkeit fuer "Reinspringen"-Effekt.
      const boosted = snapBoostRef.current > 0;
      if (boosted) snapBoostRef.current -= 1;
      const effectiveSpeed = boosted ? LERP_SPEED * 3 : LERP_SPEED;
      const factor = 1 - Math.exp(-effectiveSpeed * dt);

      const newX = maskPositionRef.current.x + deltaX * factor;
      const newY = maskPositionRef.current.y + deltaY * factor;
      maskPositionRef.current = { x: newX, y: newY };

      // Size Animation (zeitbasiert)
      const sizeDelta = targetSizeRef.current - currentSizeRef.current;
      const sizeFactor = 1 - Math.exp(-SIZE_LERP_SPEED * dt);
      currentSizeRef.current = currentSizeRef.current + sizeDelta * sizeFactor;

      // Update CSS Custom Properties
      shadow.style.setProperty("--posX", `${newX}%`);
      shadow.style.setProperty("--posY", `${newY}%`);
      shadow.style.setProperty("--size", `${currentSizeRef.current}px`);

      // Snap-Ankunft: body[data-mask-snapped] wird erst gesetzt, wenn die Maske
      // tatsaechlich am Target angekommen ist (nicht schon beim Eintritt in den
      // Snap-Radius). Dadurch wird der Farbwechsel des Kreises mit dem
      // Einschlag der Maske synchronisiert -> wirkt wie ein Ueberlagerungseffekt.
      const snappedEl = snappedTargetRef.current;
      if (snappedEl) {
        const arrived = distance < 1.2; // <1.2% Abstand zum Target (in Container-%)
        const attrSet = document.body.hasAttribute("data-mask-snapped");
        if (arrived && !attrSet) {
          const name = snappedEl.getAttribute("data-mask-snap-target") || "";
          document.body.setAttribute("data-mask-snapped", name);
        }
      }

      // Weiter animieren solange sich etwas bewegt
      const sizeDistance = Math.abs(sizeDelta);
      if (distance > 0.05 || sizeDistance > 0.3) {
        animationRef.current = requestAnimationFrame(updateMaskPosition);
      } else {
        isAnimatingRef.current = false;
        lastFrameTime = 0;
      }
    };

    // Throttled Animation Starter
    const startAnimation = () => {
      if (!isAnimatingRef.current) {
        isAnimatingRef.current = true;
        animationRef.current = requestAnimationFrame(updateMaskPosition);
      }
    };

    // Optimierte Scroll-Anpassung für Lenis
    const handleScrollUpdate = () => {
      // Container Rect aktualisieren bei Scroll
      updateContainerRect();

      const rect = containerRectRef.current;
      if (!rect) return;

      const isInView = rect.top < window.innerHeight && rect.bottom > 0;

      if (isInView && globalMousePosition.current.x !== 0) {
        // Mausposition relativ zum Container nach Scroll neu berechnen
        const relativeX =
          ((globalMousePosition.current.x - rect.left) / rect.width) * 100;
        const relativeY =
          ((globalMousePosition.current.y - rect.top) / rect.height) * 100;

        // Grenzen einhalten und Position aktualisieren
        const clampedX = Math.max(0, Math.min(100, relativeX));
        const clampedY = Math.max(0, Math.min(100, relativeY));

        mousePositionRef.current = { x: clampedX, y: clampedY };

        // Animation starten
        startAnimation();
      }
    };

    // Optimierter Mouse move handler
    const handleMouseMove = (event: MouseEvent) => {
      let { clientX, clientY } = event;

      // Globale Mausposition speichern (echter Cursor, NICHT der gesnappte Wert)
      globalMousePosition.current = { x: clientX, y: clientY };

      // Magnetic Snap prüfen: zieht die Maske auf Logo/Login-Icon wenn nah genug.
      // Cursor-Koordinaten werden überschrieben -> Mask folgt dem Target, nicht der Maus.
      const snap = findSnapTarget(clientX, clientY);
      if (snap) {
        clientX = snap.centerX;
        clientY = snap.centerY;
        setSnappedTarget(snap.element);
      } else {
        setSnappedTarget(null);
      }

      // Magnetic Pull: Icons selbst beugen sich Richtung Cursor (mit Cursor-Pos VOR Snap)
      applyMagneticPull(
        globalMousePosition.current.x,
        globalMousePosition.current.y,
        snap ? snap.element : null
      );

      // Responsive Maskengrößen: clamp(min, vw%, max)
      const isMobile = window.innerWidth < 768;
      const collisionSize = isMobile
        ? getResponsiveSize(160, 45, 220) // Smartphone: kleine Maske
        : isTabletSize()
        ? getResponsiveSize(280, 35, 360) // Tablet: mittlere Maske
        : getResponsiveSize(300, 28, 420); // Desktop: mittlere Maske
      const normalSize = getResponsiveSize(20, 1.5, 30); // Kleine Maske
      // Snap: Maske wird EXAKT so gross wie das Target (Icon-Kreis) -> sieht aus als
      // wuerde die Maske in den Kreis hineinverschwinden.
      const targetSize = snap
        ? snap.size
        : detectCollision(clientX, clientY)
        ? collisionSize
        : normalSize;

      // Container-relative Position berechnen mit gecachtem Rect
      const rect =
        containerRectRef.current || container.getBoundingClientRect();

      // Icons sitzen oberhalb des MaskEffect-Containers (fixed, top-4).
      // Snap-Targets ERZWINGEN isOverContainer=true, damit die Maske auch dann greift.
      const isOverContainer =
        snap !== null ||
        (clientX >= rect.left &&
          clientX <= rect.right &&
          clientY >= rect.top &&
          clientY <= rect.bottom);

      // Mouse Enter/Leave Detection für Size Animation
      if (isOverContainer && !isMouseInContainerRef.current) {
        // Maus betritt Container - Grow from 0
        isMouseInContainerRef.current = true;
        // targetSizeRef bleibt unverändert für normale Größe
      } else if (!isOverContainer && isMouseInContainerRef.current) {
        // Maus verlässt Container - Shrink to 0
        isMouseInContainerRef.current = false;
        // targetSizeRef wird auf 0 gesetzt für Verschwinden
      }

      if (isOverContainer) {
        const relativeX = ((clientX - rect.left) / rect.width) * 100;
        const relativeY = ((clientY - rect.top) / rect.height) * 100;

        // Grenzen einhalten
        const clampedX = Math.max(0, Math.min(100, relativeX));
        const clampedY = Math.max(0, Math.min(100, relativeY));

        // Update Mausposition
        mousePositionRef.current = { x: clampedX, y: clampedY };

        // Update Container Rect gelegentlich
        if (Math.random() < 0.01) {
          // 1% Chance pro mousemove
          updateContainerRect();
        }
      }

      // Update Zielgröße basierend auf Container Status
      if (isMouseInContainerRef.current) {
        targetSizeRef.current = targetSize; // Normale Größe oder Collision Size
      } else {
        targetSizeRef.current = 0; // Schrumpft auf 0 wenn außerhalb
      }

      // Optimierte Animation starten
      startAnimation();
    };

    // Container Mouse Enter/Leave für Size Animation
    const handleMouseEnter = () => {
      if (!isMouseInContainerRef.current) {
        isMouseInContainerRef.current = true;
        // Maske wächst von 0 auf normale Größe
        startAnimation();
      }
    };

    const handleMouseLeave = () => {
      if (isMouseInContainerRef.current) {
        isMouseInContainerRef.current = false;
        // Maske schrumpft auf 0
        targetSizeRef.current = 0;
        startAnimation();
      }
    };

    // Magnetic-Snap-Click: Wenn beim Klick ein Target gesnappt ist, navigiere dorthin -
    // auch wenn die echte Maus daneben war. Das erfüllt das UX-Versprechen der Anziehung.
    // Wenn der native Click bereits auf dem Target landete (Maus drauf), tut dieser Handler
    // nichts (der Link navigiert selbst).
    const handleSnapClick = (event: MouseEvent) => {
      const target = snappedTargetRef.current;
      if (!target) return;
      const clicked = event.target as Node | null;
      if (clicked && target.contains(clicked)) return; // Nativer Klick, nichts zu tun
      // Snap-Klick: href auslesen und programmatisch navigieren (SPA-Navigation).
      const href = target.getAttribute("href");
      if (href) {
        event.preventDefault();
        navigate(href);
      }
    };

    // Event listeners - optimiert für Lenis
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("click", handleSnapClick);
    container.addEventListener("mouseenter", handleMouseEnter);
    container.addEventListener("mouseleave", handleMouseLeave);

    // Optimierter Scroll Handler - nur bei tatsächlichem Scroll
    let scrollTimeout: number;
    const throttledScrollHandler = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = window.setTimeout(handleScrollUpdate, 16); // ~60fps
    };

    window.addEventListener("scroll", throttledScrollHandler, {
      passive: true,
    });

    // Resize Handler - aktualisiert gecachte Rects
    const handleResize = () => {
      updateContainerRect();
    };
    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("click", handleSnapClick);
      container.removeEventListener("mouseenter", handleMouseEnter);
      container.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("scroll", throttledScrollHandler);
      window.removeEventListener("resize", handleResize);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      isAnimatingRef.current = false;
      // Snap-Attribut zurücksetzen, damit es auf anderen Seiten nicht hängen bleibt
      document.body.removeAttribute("data-mask-snapped");
    };
  }, []);

  return (
    <section
      ref={containerRef}
      className={`relative w-full ${
        !isTouchDevice && !flatMode ? "cursor-none" : ""
      } ${className}`}
      style={{
        // PREVENT FOUC: Always visible to avoid hydration mismatch
        visibility: "visible",
        opacity: isInitialized ? 1 : 0,
        // Exakte Viewport-Höhe per JavaScript gemessen (zuverlässiger als CSS vh)
        height: viewportHeight ? `${viewportHeight}px` : "100vh",
        overflow: "hidden",
        // Clip-Path als zusätzliche Sicherheit gegen Überlauf
        clipPath: "inset(0)",
        WebkitClipPath: "inset(0)",
        // Touch-Geräte: Kontextmenü und Textauswahl verhindern
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        userSelect: "none",
        // Touch-Action: nur vertikales Scrollen erlauben, kein Zoom/Pan
        touchAction: "pan-y",
      }}
    >
      {/* Background Images Layer with Parallax */}
      {backgroundImages.length > 0 && (
        <div
          ref={parallaxContainerRef}
          className="absolute inset-0 w-full h-full overflow-hidden"
          style={{
            filter: `brightness(${backgroundBrightness})`,
          }}
        >
          {backgroundImages.map((image, index) => (
            <div
              key={image.src}
              className="absolute inset-0 transition-opacity duration-[2000ms] ease-in-out"
              style={{
                opacity: index === activeImageIndex ? 1 : 0,
                zIndex: index === activeImageIndex ? 1 : 0,
              }}
            >
              <img
                src={image.src}
                alt={image.alt}
                data-parallax-img=""
                data-base-transform={`scale(${image.scale || 1.1})`}
                className="w-full h-full object-cover"
                style={{
                  filter: `grayscale(100%)${
                    image.brightness ? ` brightness(${image.brightness})` : ""
                  }`,
                  transform: `translate(0px, 0px) scale(${image.scale || 1.1})`,
                  transformOrigin: "center center",
                  objectPosition:
                    image.objectPosition ||
                    `${image.positionX || "center"} ${
                      image.positionY || "center"
                    }`,
                  willChange: "transform",
                }}
              />
            </div>
          ))}
          {/* Dark overlay for better text readability */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, rgba(13,13,13,0.4) 0%, rgba(13,13,13,0.6) 50%, rgba(13,13,13,0.4) 100%)",
            }}
          />
        </div>
      )}

      {/* Normal Layer - Hintergrund */}
      <div
        className="absolute inset-0 w-full h-full transition-[background] duration-300 ease-in-out"
        style={{
          zIndex: 2,
          background:
            backgroundImages.length === 0
              ? parseGradient(normalGradient)
              : undefined,
        }}
      >
        <div className="relative w-full h-full flex items-center justify-center">
          <div className="text-center px-4 sm:px-8 lg:px-16">
            <div
              className={`change-text mask-collision-target uppercase ${
                flatMode ? "" : "cursor-none"
              } select-none m-0 p-4 sm:p-8 lg:p-16 flex flex-col items-center justify-center transition-colors duration-300`}
              style={{
                fontFamily: "'Avant Garde', sans-serif",
                fontWeight: 700,
                fontSize: "clamp(2.5rem, 8vw, 8.75rem)",
                letterSpacing: "-0.05em",
                lineHeight: "115%",
                color: textColor,
              }}
            >
              {normalText.map((word, index) => {
                // Pressed-In Effekt mit optionalem Neon-Flicker
                if (pressedInIndices.includes(index)) {
                  return (
                    <span key={index} className="block">
                      <FlickerText
                        word={word}
                        hasBitcoinGlow={bitcoinIndices.includes(index)}
                      />
                    </span>
                  );
                }

                // Normaler Text
                return (
                  <span
                    key={index}
                    className="block"
                    style={{
                      color: greyIndices.includes(index)
                        ? "#6b7280"
                        : bitcoinIndices.includes(index)
                        ? "#F7931A"
                        : undefined,
                    }}
                  >
                    {word}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Touch-Hint für Tap-and-Hold - nur auf Touch-Geräten, verschwindet wenn aktiv */}
      {isTouchDevice && !touchControlActive && (
        <div className="absolute bottom-8 left-0 right-0 flex justify-center z-20 pointer-events-none">
          <div
            className="px-5 py-3 rounded-full backdrop-blur-md flex items-center gap-3"
            style={{
              backgroundColor: "rgba(247, 147, 26, 0.15)",
              border: "1px solid rgba(247, 147, 26, 0.3)",
            }}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="#F7931A"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zM12 2.25V4.5m5.834.166l-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243l-1.59-1.59"
              />
            </svg>
            <span
              style={{
                color: "#F7931A",
                fontSize: "0.875rem",
                fontWeight: 500,
              }}
            >
              Gedrückt halten um die Maske zu steuern
            </span>
          </div>
        </div>
      )}

      {/* Shadow Layer - nur wenn nicht flatMode (sonst übernimmt der globale LandingMaskOverlay den Reveal) */}
      {!flatMode && (
        <div
          ref={shadowRef}
          className="absolute inset-0 w-full h-full pointer-events-none z-10"
          style={{
            background: parseGradient(shadowGradient),
            maskImage: `radial-gradient(circle at var(--posX) var(--posY), black calc(var(--size) / 2), transparent calc(var(--size) / 2))`,
            WebkitMaskImage: `radial-gradient(circle at var(--posX) var(--posY), black calc(var(--size) / 2), transparent calc(var(--size) / 2))`,
            transform: "translateZ(0)",
            backfaceVisibility: "hidden",
          }}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            <div className="text-center px-4 sm:px-8 lg:px-16">
              <div
                className="change-text uppercase cursor-none select-none m-0 p-4 sm:p-8 lg:p-16 flex flex-col items-center justify-center"
                style={{
                  fontFamily: "'Avant Garde', sans-serif",
                  fontWeight: 700,
                  fontSize: "clamp(2.5rem, 8vw, 8.75rem)",
                  letterSpacing: "-0.05em",
                  lineHeight: "115%",
                }}
              >
                {shadowText.map((word, index) => (
                  <span
                    key={index}
                    className="block"
                    style={{
                      color: "#080808",
                      textShadow: "0 6px 12px rgba(0,0,0,0.5)",
                    }}
                  >
                    {word}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
});

MaskEffect.displayName = "MaskEffect";
